// dwaar/src/pages/Satyapan.jsx
// Repo: terralekha-core | Folder: dwaar/src/pages/
// Satyapan — Sanskrit: सत्यापन — verification/authentication.
//   Auditor portal: view all sealed PassPorts and record assurance sign-offs.
//   Accessible only to users with role='auditor'.
//   Cross-org: auditors see PassPorts from all organisations.
//
// Data flow:
//   1. auth.getSession()                    → session + accessToken
//   2. public.users WHERE id = userId       → role check (must be 'auditor')
//   3. GET /api/satyapan/passports (Bearer) → sealed PassPorts list (cross-org via backend)
//   4. POST /api/satyapan/certify/{id}      → record ISO 14064-3 sign-off
//
// Reveal doctrine:
//   API error detail stays in console. User sees only PUBLIC_ERROR.

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getSupabaseClient }        from "../lib/supabaseClient.js";
import { BRAND_NAME, PUBLIC_ERROR } from "../lib/brand.js";
import Koshtha                      from "../components/Koshtha.jsx";

const API_BASE = import.meta.env.VITE_API_URL ?? "";

const PHASE = Object.freeze({
  LOADING: "loading",
  READY:   "ready",
  ERROR:   "error",
});

// ── PassPort card with certify action ─────────────────────────────────────────

function PassPortCard({ passport, accessToken }) {
  const [certifying, setCertifying] = useState(false);
  const [certified,  setCertified]  = useState(false);
  const [certError,  setCertError]  = useState("");

  async function handleCertify() {
    setCertifying(true);
    setCertError("");
    try {
      const res = await fetch(
        `${API_BASE}/api/satyapan/certify/${passport.passport_id}`,
        {
          method:  "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization:  `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ compliance_type: passport.compliance_type }),
        }
      );
      if (!res.ok) { setCertError(PUBLIC_ERROR); return; }
      setCertified(true);
    } catch {
      setCertError(PUBLIC_ERROR);
    } finally {
      setCertifying(false);
    }
  }

  return (
    <article className="tl-passport-card">
      <p className="tl-passport-cn">CN {passport.cn_code}</p>
      <p className="tl-passport-period">
        {passport.reporting_period_start} – {passport.reporting_period_end}
      </p>
      <span
        className={`tl-badge tl-badge--${passport.status}`}
        aria-label={`Status: ${passport.status}`}
      >
        {passport.status.toUpperCase()}
      </span>
      <span
        className={`tl-compliance-badge tl-compliance-badge--${passport.compliance_type.toLowerCase()}`}
        aria-label={`Compliance type: ${passport.compliance_type}`}
        style={{ marginLeft: "0.5rem" }}
      >
        {passport.compliance_type}
      </span>
      {passport.total_tco2e != null && (
        <p className="tl-passport-co2">
          {parseFloat(passport.total_tco2e).toFixed(2)} tCO₂e
        </p>
      )}
      {certified ? (
        <p className="tl-sub" role="status" style={{ color: "#4CAF72", marginTop: "0.5rem" }}>
          ✓ Certification recorded
        </p>
      ) : (
        <>
          <button
            className="tl-button-channel"
            aria-label={`Certify passport CN ${passport.cn_code}`}
            onClick={handleCertify}
            disabled={certifying}
            style={{ marginTop: "0.75rem" }}
          >
            {certifying ? "Recording…" : "Certify"}
          </button>
          {certError && (
            <p className="tl-error" role="alert" style={{ marginTop: "0.4rem" }}>
              {certError}
            </p>
          )}
        </>
      )}
    </article>
  );
}

// ── Satyapan ──────────────────────────────────────────────────────────────────

export default function Satyapan() {
  const navigate = useNavigate();

  const [phase,       setPhase]       = useState(PHASE.LOADING);
  const [passports,   setPassports]   = useState([]);
  const [accessToken, setAccessToken] = useState("");
  const [userEmail,   setUserEmail]   = useState("");

  const loadPassports = useCallback(async (token) => {
    try {
      const res = await fetch(`${API_BASE}/api/satyapan/passports`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`passports fetch ${res.status}`);
      const data = await res.json();
      setPassports(Array.isArray(data) ? data : []);
      setPhase(PHASE.READY);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("satyapan: passports fetch failed", err);
      setPhase(PHASE.ERROR);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const client = getSupabaseClient();
        const { data: { session } } = await client.auth.getSession();
        if (!session) { navigate("/login", { replace: true }); return; }

        const token  = session.access_token;
        const userId = session.user.id;

        const { data: userRow, error: userErr } = await client
          .from("users").select("role, full_name").eq("id", userId).single();

        if (userErr || !userRow || userRow.role !== "auditor") {
          navigate("/login", { replace: true });
          return;
        }

        if (!cancelled) {
          setAccessToken(token);
          setUserEmail(session.user?.email || "");
          await loadPassports(token);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("satyapan: load failed", err);
        if (!cancelled) setPhase(PHASE.ERROR);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [navigate, loadPassports]);

  async function handleSignOut() {
    try { await getSupabaseClient().auth.signOut(); } catch { /* swallow */ }
    navigate("/login", { replace: true });
  }

  // ── Loading ───────────────────────────────────────────────────────────────────
  if (phase === PHASE.LOADING) {
    return (
      <Koshtha role="auditor">
        <main className="tl-page tl-page--centered">
          <p className="tl-loading">Loading passports for assurance review…</p>
        </main>
      </Koshtha>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────────
  if (phase === PHASE.ERROR) {
    return (
      <Koshtha role="auditor" userEmail={userEmail} accessToken={accessToken} onSignOut={handleSignOut}>
        <main className="tl-page tl-page--centered">
          <p className="tl-error">{PUBLIC_ERROR}</p>
        </main>
      </Koshtha>
    );
  }

  // ── Ready ─────────────────────────────────────────────────────────────────────
  return (
    <Koshtha role="auditor" userEmail={userEmail} accessToken={accessToken} onSignOut={handleSignOut}>
      <main className="tl-page">

        <header className="tl-page-header">
          <h1 className="tl-org-name">Assurance Review</h1>
          <p className="tl-sub">
            Sealed PassPorts pending ISO 14064-3 assurance certification.
          </p>
        </header>

        <section aria-labelledby="satyapan-heading">
          <h2 id="satyapan-heading" className="tl-section-heading">
            Sealed PassPorts
          </h2>

          {passports.length === 0 ? (
            <div className="tl-empty">
              <p className="tl-empty-text">
                No passports pending assurance at this time.
              </p>
            </div>
          ) : (
            <div className="tl-passport-grid">
              {passports.map((pp) => (
                <PassPortCard
                  key={pp.passport_id}
                  passport={pp}
                  accessToken={accessToken}
                />
              ))}
            </div>
          )}
        </section>

      </main>
    </Koshtha>
  );
}
